<template>
  <div
    class="info-box"
    :class="[themeClass, isDark ? 'dark' : '', { 'info-box--dense': dense }, { 'is-expanded': cExpanded }]">
    <div
      :class="['col-12 row justify-between items-center q-px-xs', { 'cursor-pointer': expandable }]"
      @click="triggerIfExpanded">
      <div class="col row justify-between items-center">
        <div
          :class="[
            isDark && !labelClass ? 'label-text' : '',
            labelClass ? labelClass : 'text-overline text-weight-medium text-uppercase',
            { 'text-weight-medium': expandable },
          ]">
          <slot
            v-if="$slots['header-title']"
            name="header-title"></slot>
          <div v-if="label && label.length > 0">{{ label }}</div>
        </div>
        <!-- Center slot placed between the left label and right-side actions -->
        <div
          v-if="$slots['header-center'] || (modelId && modelName)"
          class="row justify-center">
          <slot name="header-center"></slot>
          <CrudPreview
            v-if="modelId && modelName"
            :model-id="modelId"
            :model-name="modelName"
            :btn-only="modelBtnOnly"
            :prefix="modelPrefix"
            :suffix="modelSuffix" />
        </div>
        <template v-if="!expandable || cExpanded || chipAlways">
          <div
            v-if="$slots['header-chip']"
            :class="[chipClass]">
            <slot name="header-chip"></slot>
          </div>
          <div
            v-else-if="badge"
            :class="[chipClass]">
            <q-badge :color="badgeColor">
              {{ badge }}
            </q-badge>
          </div>
        </template>
        <div v-if="isCopyEnabled">
          <q-btn
            flat
            size="sm"
            :dense="dense"
            class="info-box__copy-btn"
            :label="copyBtnLabel"
            :color="copied ? 'positive' : 'primary'"
            :icon="copied ? symRoundedCheckCircle : symRoundedContentCopy"
            :aria-label="copied ? t('core.InfoBox.copy.tooltip.copied') : t('core.InfoBox.copy.tooltip.click')"
            @click="copyToClipboard">
            <q-tooltip>
              {{ copied ? t('core.InfoBox.copy.tooltip.copied') : t('core.InfoBox.copy.tooltip.click') }}
            </q-tooltip>
          </q-btn>
        </div>
      </div>
      <div
        v-if="expandable"
        class="col-auto">
        <q-btn
          flat
          size="sm"
          square
          dense
          :icon="cExpanded ? symRoundedKeyboardArrowUp : symRoundedKeyboardArrowDown"
          :aria-label="cExpanded ? t('core.InfoBox.expand.tooltip.collapse') : t('core.InfoBox.expand.tooltip.expand')"
          @click.stop="handleToggleExpand">
          <q-tooltip>
            {{ cExpanded ? t('core.InfoBox.expand.tooltip.collapse') : t('core.InfoBox.expand.tooltip.expand') }}
          </q-tooltip>
        </q-btn>
      </div>
    </div>

    <transition
      name="expand"
      @enter="startExpandTransition"
      @after-enter="endExpandTransition"
      @before-leave="startCollapseTransition"
      @after-leave="endCollapseTransition">
      <div
        v-show="!expandable || cExpanded"
        class="info-box__content-wrapper q-px-sm">
        <div class="row">
          <div v-if="$slots['body-before']">
            <slot name="body-before"></slot>
          </div>
          <div class="col">
            <div class="info-box__content-section">
              <div
                v-if="subtitle"
                class="text-subtitle1">
                {{ subtitle }}
              </div>
              <template v-if="text !== false && (text || copyValue) && !$slots.body && !$slots.default">
                <div
                  v-if="html"
                  class="info-box__content-wrapper text-subtitle2"
                  v-html="text || copyValue" />
                <div
                  v-else
                  class="info-box__content-wrapper text-subtitle2">
                  {{ text || copyValue }}
                </div>
              </template>

              <div
                v-else-if="($slots.body || $slots.default) && ((expandable && cExpanded) || !expandable)"
                class="col-12">
                <div class="fit">
                  <slot
                    v-if="$slots.body"
                    name="body"></slot>
                  <slot
                    v-else
                    name="default"></slot>
                </div>
              </div>

              <div
                v-else-if="text !== false"
                class="info-box__content-wrapper text-grey-6">
                -
                <ToolTip>{{ t('core.InfoBox.noData') }}</ToolTip>
              </div>
              <div
                v-if="caption || $slots['body-caption']"
                class="info-box__caption q-mt-xs q-ml-xs"
                :class="[isDark ? 'text-grey-5' : 'text-grey-7']">
                <template v-if="caption">{{ caption }}</template>
                <slot
                  v-else
                  name="body-caption"></slot>
              </div>
              <ToolTip v-if="tooltip">{{ tooltip }}</ToolTip>
            </div>
          </div>
          <div v-if="$slots['body-after']">
            <slot name="body-after"></slot>
          </div>
        </div>
      </div>
    </transition>
    <div
      v-if="$slots['card-footer']"
      class="info-box__card-footer">
      <slot name="card-footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * InfoBox component and related exports
 * Themeable container for displaying structured information with expansion and copy functionality
 * @module InfoBox
 * @public
 * @useWhen need to display labeled information; want expandable sections; need clipboard functionality; want theme support
 */
import { symRoundedCheckCircle, symRoundedContentCopy, symRoundedKeyboardArrowDown, symRoundedKeyboardArrowUp } from '@quasar/extras/material-symbols-rounded'
import { useTheme } from 'src/CrudManager/Core/composables/useTheme'
import CrudPreview from 'src/CrudManager/Core/composites/Editors/CrudPreview/CrudPreview.vue'
import { onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfoBoxGroup } from './composables/useInfoBoxGroup'
import { useInfoBoxLogic } from './composables/useInfoBoxLogic'
import { useInfoBoxState } from './composables/useInfoBoxState'
import { useInfoBoxTransitions } from './composables/useInfoBoxTransitions'
import { INFO_BOX_DEFAULTS } from './constants'
import './InfoBox.scss'
import type { InfoBoxEmits, InfoBoxProps } from './types'

const props = withDefaults(defineProps<InfoBoxProps>(), {
  label: INFO_BOX_DEFAULTS.LABEL,
  subtitle: INFO_BOX_DEFAULTS.SUBTITLE,
  theme: INFO_BOX_DEFAULTS.THEME,
  valueClass: INFO_BOX_DEFAULTS.VALUE_CLASS,
  dense: INFO_BOX_DEFAULTS.DENSE,
  caption: INFO_BOX_DEFAULTS.CAPTION,
  badge: INFO_BOX_DEFAULTS.BADGE,
  badgeColor: INFO_BOX_DEFAULTS.BADGE_COLOR,
  copyable: INFO_BOX_DEFAULTS.COPYABLE,
  copyValue: INFO_BOX_DEFAULTS.COPY_VALUE,
  copyBtnLabel: INFO_BOX_DEFAULTS.COPY_BTN_LABEL,
  notifyDuration: INFO_BOX_DEFAULTS.NOTIFY_DURATION,
  html: INFO_BOX_DEFAULTS.HTML,
  modelId: INFO_BOX_DEFAULTS.MODEL_ID,
  modelName: INFO_BOX_DEFAULTS.MODEL_NAME,
  modelBtnOnly: INFO_BOX_DEFAULTS.MODEL_BTN_ONLY,
  modelPrefix: INFO_BOX_DEFAULTS.MODEL_PREFIX,
  modelSuffix: INFO_BOX_DEFAULTS.MODEL_SUFFIX,
  tooltip: INFO_BOX_DEFAULTS.TOOLTIP,
  expandable: INFO_BOX_DEFAULTS.EXPANDABLE,
  defaultExpanded: INFO_BOX_DEFAULTS.DEFAULT_EXPANDED,
  labelClass: INFO_BOX_DEFAULTS.LABEL_CLASS,
  textClass: INFO_BOX_DEFAULTS.TEXT_CLASS,
  chipClass: INFO_BOX_DEFAULTS.CHIP_CLASS,
  chipAlways: INFO_BOX_DEFAULTS.CHIP_ALWAYS,
  modelValue: INFO_BOX_DEFAULTS.MODEL_VALUE,
})

const emit = defineEmits<InfoBoxEmits>()
const { t } = useI18n()

const { copied, isClipboardSupported, isCopyEnabled, isDark, theme, cExpanded, thisInstance } = useInfoBoxState(props, emit)
const { themeClass } = useTheme(theme)
const { copyToClipboard, handleToggleExpand, triggerIfExpanded } = useInfoBoxLogic(props, copied, isClipboardSupported, cExpanded)
const { startExpandTransition, endExpandTransition, startCollapseTransition, endCollapseTransition } = useInfoBoxTransitions()
const { notifyExpand, clearActive } = useInfoBoxGroup()

onMounted(() => {
  if (props.group && cExpanded.value) {
    notifyExpand(props.group, thisInstance)
  }
})

onUnmounted(() => {
  if (props.group) {
    clearActive(props.group, thisInstance)
  }
})
</script>

<style scoped>
.content-wrapper {
  cursor: text !important;
  padding-left: 8px;
}

.content-wrapper :deep(*) {
  cursor: text !important;
}

.copy-icon {
  opacity: 0.8;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer !important;
  padding: 4px;
  border-radius: 4px;
  min-width: 24px;
}

.copy-icon:hover,
.copy-icon.is-hovering {
  opacity: 0.8;
  background-color: rgba(0, 0, 0, 0.05);
}

.copy-icon:active {
  transform: scale(0.95);
  background-color: rgba(0, 0, 0, 0.1);
}

.caption {
  font-size: 0.75rem;
  line-height: 1.2;
  opacity: 0.85;
}

.info-box__content-wrapper {
  position: relative;
}

.info-box__content-section {
  padding-top: 4px;
}

.info-box__card-footer {
  margin-top: auto;
  margin-left: calc(var(--info-box-padding-x, 12px) * -1);
  margin-right: calc(var(--info-box-padding-x, 12px) * -1);
  margin-bottom: calc(var(--info-box-padding-y, 10px) * -1);
  border-bottom-left-radius: var(--info-box-radius, 12px);
  border-bottom-right-radius: var(--info-box-radius, 12px);
  overflow: hidden;
  width: auto;
  display: flex;
  flex-direction: column;
}

/* A configured footer component may render nothing for a record; drop the strip then. */
.info-box__card-footer:empty {
  display: none;
}

.expand-enter-active,
.expand-leave-active {
  transition:
    height 0.3s ease-out,
    opacity 0.25s ease-out;
  will-change: height, opacity;
}

.expand-enter-from,
.expand-leave-to {
  height: 0;
  opacity: 0;
}
</style>
